import axios from 'api';

// Post-conversation analytics (PCM) — super-admin endpoints, see
// PCM_ANALYTICS_API.md §1–§5. PCM is generated automatically when a
// conversation closes; these calls cover the ones that slipped through.
//
//   GET  /api/super/conversation-analytics/summary/                              §1
//   GET  /api/super/tenants/<schema>/conversation-analytics/                     §2
//   GET  /api/super/tenants/<schema>/conversations/<id>/analytics/               §3
//   POST /api/super/tenants/<schema>/conversations/<id>/regenerate-analytics/    §4
//   POST /api/super/tenants/<schema>/conversations/regenerate-missing-analytics/ §5
//
// Session cookie + X-CSRFToken are attached by the shared axios instance.
const BASE = '/api/super';

const tenantBase = (schema) => `${BASE}/tenants/${encodeURIComponent(schema)}`;

// Drops unset values so the backend applies its own defaults (last 7 days,
// all channels, no test conversations). Lists go over as comma-separated
// strings, which is what every list-valued query param here expects.
const toQuery = (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value == null || value === '' || value === false) return;
    if (Array.isArray(value)) {
      if (value.length) params.set(key, value.join(','));
      return;
    }
    params.set(key, value);
  });
  return params;
};

// Filters: date_from, date_to, channel[], schema_name[], include_test.
export const getPcmSummary = (filters, signal) =>
  axios.get(`${BASE}/conversation-analytics/summary/`, { params: toQuery(filters), signal });

// Filters: the §1 window plus analytics_status[], has_ai_agent, ai_agent_id,
// conversation_id, intent, disposition, page, page_size (max 50).
// The paginated response is NOT wrapped in the { error, data, message }
// envelope — `unwrap` from utils/notify handles both shapes.
export const getPcmConversations = (schema, filters, signal) =>
  axios.get(`${tenantBase(schema)}/conversation-analytics/`, { params: toQuery(filters), signal });

export const getPcmConversation = (schema, id, signal) =>
  axios.get(`${tenantBase(schema)}/conversations/${encodeURIComponent(id)}/analytics/`, { signal });

// Body: { force } — force re-runs a conversation that already has PCM, or one
// stuck as in_progress. Returns 202; the result lands asynchronously.
export const regeneratePcmConversation = (schema, id, body) =>
  axios.post(`${tenantBase(schema)}/conversations/${encodeURIComponent(id)}/regenerate-analytics/`, body || {});

// Body: { date_from, date_to, channel, limit, dry_run, include_test, before_id }.
// Every queued conversation is one model call — dry-run first.
export const regenerateMissingPcm = (schema, body) =>
  axios.post(`${tenantBase(schema)}/conversations/regenerate-missing-analytics/`, body || {});
